import { faTimes, IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent, DialogOverlay, DialogProps } from '@reach/dialog';
import { FC, useEffect } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { useWindowRef } from 'src/components/window';
import { useId } from 'src/hooks/use-id';
import { ParentZIndexProvider } from 'src/hooks/use-parent-z-index';
import { Colors } from 'src/styles';
import styled, { createGlobalStyle } from 'styled-components';

export type Size = 'default' | 'small' | 'middle';

const borderRadius = '0.6rem';

const padding = '1.5rem';

const Header = styled.div<{ hasIcon: boolean }>`
  border-top-left-radius: ${borderRadius};
  border-top-right-radius: ${borderRadius};
  background-color: ${Colors.brand};
  color: white;

  display: grid;
  grid-auto-flow: column;
  grid-gap: ${padding};
  padding: ${padding};
  align-items: center;

  ${({ hasIcon }) => `
    grid-template-columns: auto ${hasIcon ? '1fr' : 'min-content'};
  `}

  font-size: 2rem;
`;

const Content = styled.div`
  border-bottom-left-radius: ${borderRadius};
  border-bottom-right-radius: ${borderRadius};
  background-color: white;
  padding: ${padding};
`;

const Title = styled.p`
  font-weight: 900;
  margin: 0;
  word-break: break-all;
`;

export const Close = styled.button`
  align-self: start;
  color: ${Colors.brandLight1};
  background: none;
  border: none;
  padding: 0;
  font-size: 2.2rem;
  justify-items: space-around;

  cursor: pointer;
  user-select: none;

  transition: color 0.35s, background-color 0.35s;

  &:hover {
    color: ${Colors.brandLight2};
  }
`;

// see https://github.com/reach/reach-ui/issues/98#issuecomment-547897104
const layoutFix = 'prisma-layout-fix-for-react-dialog';

const LayoutFix = createGlobalStyle`
  html.${layoutFix} {
    overflow-x: visible;

    & body {
      margin-right: 0 !important;
    }
  }
`;

const sizeWidths: Record<Size, string> = {
  default: '60rem',
  small: '30rem',
  middle: '45rem',
};

const ModalContent = styled(DialogContent)<{ size: Size }>`
  background: none;
  width: ${({ size }) => sizeWidths[size]};
  max-width: calc(100% - ${padding} * 2);
  padding: 0;
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.5);
  border-radius: ${borderRadius};
`;

type Props = {
  icon?: IconDefinition | undefined;
  title: string;
  size?: Size;
} & DialogProps;

export const Modal: FC<Props> = ({
  icon,
  title,
  children,
  size = 'default',
  ...dialogProps
}) => {
  useEffect(() => {
    document.documentElement.classList.add(layoutFix);
    return () => document.documentElement.classList.remove(layoutFix);
  }, []);
  const id = useId();
  const zIndex = 3;
  const windowRef = useWindowRef();
  return (
    <DialogOverlay
      {...dialogProps}
      style={{
        zIndex,
      }}
      dangerouslyBypassScrollLock // we provide our own <RemoveScroll/> to make use of shards
    >
      <RemoveScroll shards={windowRef ? [windowRef] : []}>
        <ParentZIndexProvider value={zIndex}>
          <LayoutFix />
          <ModalContent aria-labelledby={id} size={size}>
            {title && (
              <Header hasIcon={Boolean(icon)}>
                {icon && <FontAwesomeIcon icon={icon} />}
                <Title id={id}>{title}</Title>
                <Close onClick={dialogProps.onDismiss}>
                  <FontAwesomeIcon icon={faTimes} aria-label="Close modal" />
                </Close>
              </Header>
            )}

            <Content>{children}</Content>
          </ModalContent>
        </ParentZIndexProvider>
      </RemoveScroll>
    </DialogOverlay>
  );
};

export const ModalDivider = styled.hr`
  position: relative;
  left: -${padding};
  width: calc(100% + ${padding} * 2);
  border: none;
  border-top: 0.1rem solid #e5e5e5;
`;
